import React, { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Pagination from "@mui/material/Pagination";

import CommonGroupBar from "src/components/commonCode/CommonCodeGroupBar";
import CommonCodeFilterBar from "src/components/commonCode/CommonCodeFilterBar";
import CommonCodeListTable from "src/components/commonCode/CommonCodeListTable";
import CommonCodeGroupUpsertModal from "src/components/commonCode/CommonCodeGroupUpsertModal";
import CommonCodeUpsertModal from "src/components/commonCode/CommonCodeUpsertModal";

import { useCommonCodeStore } from "src/store/commonCode/commonCode.store";
import {
  selectCommonError,
  selectCommonLoading,
  selectGroupList,
  selectGroupQuery,
  selectGroupTotalElements,
  selectGroupTotalPages,
  selectSelectedGrpCode,
  selectCodeList,
  selectCodeQuery,
  selectCodeTotalElements,
  selectCodeTotalPages,
} from "src/store/commonCode/commonCode.selectors";

import type { CommonGroupItem, CommonCodeItem } from "src/types/common/commonCodeTypes";

export default function CommonCodeManagePage() {
  const loading = useCommonCodeStore(selectCommonLoading);
  const error = useCommonCodeStore(selectCommonError);

  /** ===== groups ===== */
  const groupQuery = useCommonCodeStore(selectGroupQuery);
  const groups = useCommonCodeStore(selectGroupList);
  const groupTotalPages = useCommonCodeStore(selectGroupTotalPages);
  const groupTotalElements = useCommonCodeStore(selectGroupTotalElements);

  /** ===== codes ===== */
  const selectedGrpCode = useCommonCodeStore(selectSelectedGrpCode);
  const codeQuery = useCommonCodeStore(selectCodeQuery);
  const codes = useCommonCodeStore(selectCodeList);
  const codeTotalPages = useCommonCodeStore(selectCodeTotalPages);
  const codeTotalElements = useCommonCodeStore(selectCodeTotalElements);

  /** ===== actions ===== */
  const setGroupQuery = useCommonCodeStore((s) => s.setGroupQuery);
  const fetchGroups = useCommonCodeStore((s) => s.fetchGroups);
  const setSelectedGrpCode = useCommonCodeStore((s) => s.setSelectedGrpCode);

  const setCodeQuery = useCommonCodeStore((s) => s.setCodeQuery);
  const resetCodeQuery = useCommonCodeStore((s) => s.resetCodeQuery);
  const fetchCodes = useCommonCodeStore((s) => s.fetchCodes);

  const createCode = useCommonCodeStore((s) => s.createCode);
  const updateCode = useCommonCodeStore((s) => s.updateCode);
  const deleteCode = useCommonCodeStore((s) => s.deleteCode);

  /** ===== modals ===== */
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState<"create" | "edit">("create");
  const [editingGroup, setEditingGroup] = useState<CommonGroupItem | null>(null);

  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeModalMode, setCodeModalMode] = useState<"create" | "edit">("create");
  const [editingCode, setEditingCode] = useState<CommonCodeItem | null>(null);

  /** ✅ 마운트 때 그룹 1번만 로드 (자동 재조회 useEffect 제거) */
  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ✅ groups 배열 참조 의존 X: 첫 그룹 code 문자열만 의존 */
  const firstGrpCode = groups?.[0]?.grpCode || "";
  useEffect(() => {
    if (selectedGrpCode) return;
    if (!firstGrpCode) return;
    setSelectedGrpCode(firstGrpCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstGrpCode, selectedGrpCode]);

  const groupUseYnUi = useMemo(() => {
    if (typeof groupQuery.useYn !== "boolean") return "";
    return groupQuery.useYn ? "true" : "false";
  }, [groupQuery.useYn]);

  const codeUseYnUi = useMemo(() => {
    if (typeof codeQuery.useYn !== "boolean") return "";
    return codeQuery.useYn ? "true" : "false";
  }, [codeQuery.useYn]);

  const codeDisabled = !selectedGrpCode;

  const openCreateGroup = () => {
    setGroupModalMode("create");
    setEditingGroup(null);
    setGroupModalOpen(true);
  };

  const openEditGroup = (item: CommonGroupItem) => {
    setGroupModalMode("edit");
    setEditingGroup(item);
    setGroupModalOpen(true);
  };

  const openCreateCode = () => {
    setCodeModalMode("create");
    setEditingCode(null);
    setCodeModalOpen(true);
  };

  const openEditCode = (item: CommonCodeItem) => {
    setCodeModalMode("edit");
    setEditingCode(item);
    setCodeModalOpen(true);
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <Box sx={{ overflowX: "auto" }}>
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            minWidth: 980,
            width: "max-content",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            공통코드 관리
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* ===== 그룹 선택/검색 ===== */}
          <CommonGroupBar
            groups={groups}
            selectedGrpCode={selectedGrpCode}
            grpCode={groupQuery.grpCode || ""}
            grpName={groupQuery.grpName || ""}
            useYn={groupUseYnUi as any}
            size={groupQuery.size}
            onChangeGroupQuery={(patch) => setGroupQuery({ ...patch, page: 0 })}
            onSearchGroups={async () => {
              setGroupQuery({ page: 0 });
              await fetchGroups();
            }}
            onResetGroups={async () => {
              setGroupQuery({
                ...groupQuery,
                grpCode: "",
                grpName: "",
                useYn: undefined,
                page: 0,
              });
              await fetchGroups();
            }}
            onSelectGroup={(grpCode) => {
              setSelectedGrpCode(grpCode);
              resetCodeQuery();
            }}
            onSearchCodes={async () => {
              setCodeQuery({ page: 0 });
              await fetchCodes();
            }}
            onCreateGroup={openCreateGroup}
            onEditGroup={openEditGroup as any}
          />

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              그룹 총 {groupTotalElements}건
            </Typography>

            <Pagination
              count={Math.max(1, groupTotalPages)}
              page={Math.min(groupQuery.page + 1, Math.max(1, groupTotalPages))}
              onChange={async (_, v) => {
                setGroupQuery({ page: v - 1 });
                await fetchGroups();
              }}
              size="small"
              shape="rounded"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* ===== 코드 검색/생성 ===== */}
          <CommonCodeFilterBar
            code={codeQuery.code || ""}
            codeName={codeQuery.codeName || ""}
            useYn={codeUseYnUi as any}
            size={codeQuery.size}
            disabled={codeDisabled}
            onChangeQuery={(patch) => setCodeQuery({ ...patch, page: 0 })}
            onSearch={fetchCodes}
            onReset={async () => {
              resetCodeQuery();
              await fetchCodes();
            }}
            onCreate={openCreateCode}
          />

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "text.secondary" }}>
              선택 그룹: {selectedGrpCode || "-"} / 코드 총 {codeTotalElements}건
            </Typography>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          ) : null}

          <Box sx={{ minWidth: 980 }}>
            <CommonCodeListTable
              list={codes}
              onEdit={openEditCode}
              onDelete={async (item) => {
                if (!window.confirm("삭제할까요?")) return;
                await deleteCode(item.codeId);
              }}
            />
          </Box>

          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <Pagination
              count={Math.max(1, codeTotalPages)}
              page={Math.min(codeQuery.page + 1, Math.max(1, codeTotalPages))}
              onChange={(_, v) => setCodeQuery({ page: v - 1 })}
              shape="rounded"
              showFirstButton
              showLastButton
              disabled={loading || codeDisabled}
            />
          </Stack>
        </Paper>
      </Box>

      <CommonCodeGroupUpsertModal
        open={groupModalOpen}
        mode={groupModalMode}
        item={editingGroup}
        onClose={() => setGroupModalOpen(false)}
      />

      <CommonCodeUpsertModal
        open={codeModalOpen}
        mode={codeModalMode}
        grpCode={selectedGrpCode}
        item={editingCode}
        loading={loading}
        onClose={() => setCodeModalOpen(false)}
        onSubmitCreate={async (payload) => await createCode(payload)}
        onSubmitEdit={async (payload) => await updateCode(payload)}
      />
    </Box>
  );
}
